import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

interface ModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  closeOnOverlay?: boolean;
  width?: number;
  labelledBy?: string;
  describedBy?: string;
}

const panels: HTMLDivElement[] = [];
let originalOverflow = '';
const focusableSelector = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  children,
  closeOnOverlay = true,
  width = 420,
  labelledBy,
  describedBy,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    if (panels.length === 0) {
      originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    panels.push(panel);
    // Focus the dialog itself to keep the heading visible and avoid opening
    // the mobile keyboard or preselecting a destructive confirmation.
    panel.focus({ preventScroll: true });
    const isTopPanel = () => panels[panels.length - 1] === panel;
    const onKey = (e: KeyboardEvent) => {
      if (!isTopPanel()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeRef.current?.();
      }
      if (e.key === 'Tab') {
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector))
          .filter((element) => !element.closest('[hidden], [aria-hidden="true"]') &&
            getComputedStyle(element).display !== 'none' && getComputedStyle(element).visibility !== 'hidden');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) {
          e.preventDefault();
          panel.focus();
        } else if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const onFocus = (e: FocusEvent) => {
      if (isTopPanel() && !panel.contains(e.target as Node)) panel.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    return () => {
      const wasTopPanel = isTopPanel();
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus);
      const index = panels.indexOf(panel);
      if (index !== -1) panels.splice(index, 1);
      if (panels.length === 0) document.body.style.overflow = originalOverflow;
      if (wasTopPanel && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose?.();
      }}
    >
      <Panel
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : '대화상자'}
        aria-describedby={describedBy}
        $width={width}
      >
        {children}
      </Panel>
    </Overlay>,
    document.body
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.15s ease;

  @media (max-width: 600px) {
    padding: 12px;
    padding-bottom: max(12px, env(safe-area-inset-bottom, 0px));
  }
`;

const Panel = styled.div<{ $width: number }>`
  width: 100%;
  max-width: ${({ $width }) => $width}px;
  max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: ${popIn} 0.18s ease;

  &:focus { outline: none; }

  @media (max-width: 600px) {
    max-height: calc(100dvh - 24px - env(safe-area-inset-bottom, 0px));
  }
`;
